"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cn } from "../../utils"

/**
 * Platform-wide tab bar.
 *
 * Visual: underline-style — flat row of trigger buttons separated from the
 * content by a 1px border. The active trigger lifts to the primary brand
 * colour with a 2px underline that overlaps the row border (`-mb-px`).
 * Inactive triggers sit in muted text and lift to foreground on hover.
 *
 * Icons: pass them as children alongside the label, e.g.
 *
 *   <TabsTrigger value="scenarios"><Plus size={13} /> Scenarios</TabsTrigger>
 *
 * The trigger is `inline-flex` with `gap-1.5` so the icon + label spacing
 * works without any extra wrapper.
 *
 * Overflow: many tabs on a narrow viewport are scrolled inside the list
 * (`overflow-x-auto no-scrollbar`) instead of pushing the page wider.
 * `shrink-0` on the trigger is what makes that work — without it the
 * triggers would compress and the overflow wouldn't kick in.
 */

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ ref }
    className={ cn(
      "flex items-center gap-1 border-b border-border",
      "max-w-full overflow-x-auto no-scrollbar",
      className
    ) }
    { ...props }
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ ref }
    className={ cn(
      "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap",
      "px-3 py-2 text-sm font-medium",
      "border-b-2 border-transparent -mb-px",
      "ring-offset-background transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      // Inactive
      "text-muted-foreground hover:text-foreground",
      // Active — high-contrast `foreground` text + a brand-coloured underline.
      // Using foreground (not the brand tint) for the label keeps it legible in
      // dark mode and with saturated accents; the underline + weight carry the
      // brand signal.
      "data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:font-semibold",
      className
    ) }
    { ...props }
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ ref }
    className={ cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ) }
    { ...props }
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
